import { useRef, useMemo, useEffect } from "react";
import { Color, Vector3, Box3, MeshBasicMaterial } from "three";
import { degToRad } from "three/src/math/MathUtils";
import { useSpring, animated as a, config as springConfig } from "@react-spring/three";
import RoundedRectLineGeometry from "./RoundedRectLineGeometry";

const lineWidth = 0.04;
const cornerLineWidth = 0.12;
const cornerLength = 1;

const SelectionMarker = ({ children, lineWidth = 0.04, cornerLineWidth = 0, cornerLength = 1, lineMargin = 0.15, ...props }) => {
  const groupRef = useRef(null);

  const markerMaterial = useMemo(() => {
    return new MeshBasicMaterial({
      color: new Color(102, 204, 153).multiplyScalar(0.03),
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
      toneMapped: false,
    });
  }, []);

  const bbScale = useMemo(() => {
    const bbScale = new Vector3();
    if (groupRef.current) {
      const bbox = new Box3().setFromObject(groupRef.current);
      bbox.getSize(bbScale);
    }
    return bbScale;
  }, [groupRef.current]);

  const spring = useSpring({
    opacity: props.selected ? 1 : 0,
    scaleZ: props.selected ? [1, 1, 1] : [1, 1, 0],
    scaleX: props.selected ? [1, 1, 1] : [0, 1, 1],
    ringScale: props.selected ? 1 : 0.95,
    onChange: ({ value }) => {
      markerMaterial.opacity = value.opacity;
    },
    config: { clamp: true, ...springConfig.default },
  });

  return (
    <group position={props.position} visible={props.visible}>
      <group ref={groupRef}>{children}</group>
      <group rotation={[degToRad(-90), 0, 0]}>
        <a.mesh material={markerMaterial} scale={spring.ringScale} renderOrder={2}>
          <RoundedRectLineGeometry
            lineWidth={lineWidth}
            width={bbScale.z + lineMargin}
            lenght={bbScale.x + lineMargin}
            innerRadius={0.04}
          />
        </a.mesh>
      </group>

      {/* <group>
        <mesh
          rotation={[degToRad(-90), 0, 0]}
          position={[bbScale.x / 2 + lineMargin, 0, 0]}
          material={markerMaterial}
        >
          <planeGeometry
            args={[lineWidth, bbScale.z + lineMargin * 2, 1, 1]}
          />
        </mesh>
        <mesh
          rotation={[degToRad(-90), 0, 0]}
          position={[-bbScale.x / 2 - lineMargin, 0, 0]}
          material={markerMaterial}
        >
          <planeGeometry
            args={[lineWidth, bbScale.z + lineMargin * 2, 1, 1]}
          />
        </mesh>
        <mesh
          rotation={[degToRad(-90), 0, 0]}
          position={[0, 0, bbScale.z / 2 + lineWidth / 2 + lineMargin]}
          material={markerMaterial}
        >
          <planeGeometry
            args={[bbScale.x + lineWidth + lineMargin * 2, lineWidth, 1, 1]}
          />
        </mesh>
        <mesh
          rotation={[degToRad(-90), 0, 0]}
          position={[0, 0, -(bbScale.z / 2 + lineWidth / 2 + lineMargin)]}
          material={markerMaterial}
        >
          <planeGeometry
            args={[bbScale.x + lineWidth + lineMargin * 2, lineWidth, 1, 1]}
          />
        </mesh>

        <group>
          <a.group
            scale={spring.scaleZ}
            position={[
              bbScale.x / 2 + cornerLineWidth / 2 + lineWidth / 2,
              0,
              bbScale.z / 2 + cornerLineWidth + lineWidth,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, 0]}
              position={[0, 0, -cornerLength / 2]}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>
          <a.group
            scale={spring.scaleX}
            position={[
              bbScale.x / 2 + lineWidth / 2 + cornerLineWidth,
              0,
              bbScale.z / 2 + lineMargin + lineWidth + cornerLineWidth / 2,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, degToRad(90)]}
              position={[-cornerLength / 2, 0, 0]}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>

          <a.group
            scale={spring.scaleZ}
            position={[
              bbScale.x / 2 + cornerLineWidth / 2 + lineWidth / 2,
              0,
              -bbScale.z / 2 - cornerLineWidth - lineWidth,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, 0]}
              position={[0, 0, cornerLength / 2]}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>
          <a.group
            scale={spring.scaleX}
            position={[
              bbScale.x / 2 + lineWidth / 2 + cornerLineWidth,
              0,
              -bbScale.z / 2 - lineMargin - lineWidth - cornerLineWidth / 2,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, degToRad(90)]}
              position={[-cornerLength / 2, 0, 0]}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>

          <a.group
            scale={spring.scaleZ}
            position={[
              -bbScale.x / 2 - cornerLineWidth / 2 - lineWidth / 2,
              0,
              bbScale.z / 2 + cornerLineWidth + lineWidth,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, 0]}
              position={[0, 0, -cornerLength / 2]}
              // material={markerMaterial}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>
          <a.group
            scale={spring.scaleX}
            position={[
              -bbScale.x / 2 - lineWidth / 2 - cornerLineWidth,
              0,
              bbScale.z / 2 + lineMargin + lineWidth + cornerLineWidth / 2,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, degToRad(90)]}
              position={[cornerLength / 2, 0, 0]}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>

          <a.group
            scale={spring.scaleZ}
            position={[
              -bbScale.x / 2 - cornerLineWidth / 2 - lineWidth / 2,
              0,
              -bbScale.z / 2 - cornerLineWidth - lineWidth,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, 0]}
              position={[0, 0, cornerLength / 2]}
              // material={markerMaterial}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>
          <a.group
            scale={spring.scaleX}
            position={[
              -bbScale.x / 2 - lineWidth / 2 - cornerLineWidth,
              0,
              -bbScale.z / 2 - lineMargin - lineWidth - cornerLineWidth / 2,
            ]}
          >
            <mesh
              rotation={[degToRad(-90), 0, degToRad(90)]}
              position={[cornerLength / 2, 0, 0]}
              // material={markerMaterial}
            >
              <meshBasicMaterial color="white" />
              <planeGeometry
                args={[cornerLineWidth, cornerLength, 1, 1]}
              />
            </mesh>
          </a.group>
        </group>
      </group> */}
    </group>
  );
};

export default SelectionMarker;
